import React, { useState } from 'react';
import axios from 'axios';
import {
    BuildingOfficeIcon,
    MapPinIcon,
    CheckBadgeIcon,
    ExclamationCircleIcon,
    TicketIcon,
    ShieldCheckIcon,
    ArrowRightIcon,
    ArrowLeftIcon,
    CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import { countries } from '../utils/countries';

const API_URL = '/api/v1';

const StepMarker = ({ step, current, label }) => (
    <div className={`flex flex-col items-center gap-2 ${step <= current ? 'text-[#155e75]' : 'text-gray-300'}`}>
        <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-bold text-xs transition-all ${step === current ? 'border-[#155e75] bg-blue-50' : step < current ? 'border-emerald-500 bg-emerald-50 text-emerald-600' : 'border-gray-200'}`}>
            {step < current ? '✓' : step}
        </div>
        <span className="text-[9px] font-black uppercase tracking-widest hidden md:block">{label}</span>
    </div>
);

export default function RegisterDevice() {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        deviceName: '',
        gsrn: '',
        techType: 'T010000',
        energySource: 'F01010101',
        capacity: '',
        address: '',
        city: '',
        country: 'Loading Domain...',
        meterManufacturer: '',
        meterModel: '',
        meterSerial: '',
        auxiliaryDeduction: true,
        commissionedDate: '',
        supportScheme: 'NONE',
        labelScheme: 'NONE',
        isAutoproduction: false,
        isDirectLine: false,
        h2Ready: false,
        gridOperatorName: 'ONEE (National Grid)'
    });

    React.useEffect(() => {
        axios.get(`${API_URL}/accounts/profile/me`)
            .then(res => {
                if (res.data && res.data.country_code) {
                    setFormData(prev => ({ ...prev, country: res.data.country_code }));
                } else {
                    setFormData(prev => ({ ...prev, country: 'EU' }));
                }
            })
            .catch(() => {
                setFormData(prev => ({ ...prev, country: 'EU' }));
            });
    }, []);

    const handleChange = (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setFormData({ ...formData, [e.target.name]: value });
    };

    const nextStep = () => setStep(s => s + 1);
    const prevStep = () => setStep(s => s - 1);

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);

        const email = localStorage.getItem('userEmail') || 'admin@map.go';
        const role = localStorage.getItem('userRole') || 'ADMIN';
        const headers = { 'X-User-Email': email, 'X-User-Role': role };

        try {
            const profileRes = await axios.get(`${API_URL}/accounts/profile/me`, { headers });
            const resolvedOwnerId = profileRes.data.id;

            const payload = {
                device_code: `MAE-${Date.now()}`,
                device_name: formData.deviceName,
                gsrn: formData.gsrn,
                capacity_mw: parseFloat(formData.capacity) || 0,
                energy_source_code: formData.energySource,
                technology_code: formData.techType,
                commissioned_date: formData.commissionedDate,
                country_code: formData.country,
                owner_id: resolvedOwnerId,
                address_street: formData.address,
                city: formData.city,
                meter_manufacturer: formData.meterManufacturer,
                meter_model: formData.meterModel,
                meter_serial: formData.meterSerial,
                auxiliary_power_deduction: formData.auxiliaryDeduction,
                support_scheme_type: formData.supportScheme,
                label_scheme: formData.labelScheme,
                is_autoproduction: formData.isAutoproduction,
                is_direct_line: formData.isDirectLine,
                h2_ready: formData.h2Ready,
                grid_operator_name: formData.gridOperatorName
            };
            await axios.post(`${API_URL}/devices`, payload, { headers });
            setSuccess(true);
        } catch (err) {
            let errorMsg = 'Validation Error: Check your technical manifest fields.';
            const detail = err.response?.data?.detail;
            if (detail) {
                if (Array.isArray(detail)) {
                    errorMsg = detail.map(d => `${d.loc[d.loc.length - 1]}: ${d.msg}`).join(', ');
                } else if (typeof detail === 'string') {
                    errorMsg = detail;
                }
            }
            setError(errorMsg);
            setStep(1);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="max-w-xl mx-auto py-20 text-center animate-fade-in">
                <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md border border-emerald-100">
                    <CheckBadgeIcon className="w-12 h-12 text-emerald-500" />
                </div>
                <h2 className="text-3xl font-black text-gray-800 mb-4 tracking-tighter uppercase">Registration Propagated</h2>
                <p className="text-gray-500 mb-8 leading-relaxed font-medium">
                    The technical manifest for <strong>{formData.deviceName}</strong> has been transmitted to the central Hub.
                    Asset qualification is now pending regulatory multi-sig approval.
                </p>
                <button onClick={() => window.location.href = '/devices'} className="bg-[#155e75] text-white px-10 py-4 rounded-sm font-black uppercase tracking-widest shadow-xl hover:bg-[#0e4e61] transition-all">
                    Return to Asset Index
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto py-10 px-6">
            <div className="mb-12 flex justify-between items-center max-w-2xl mx-auto border-b border-gray-100 pb-8">
                <StepMarker step={1} current={step} label="General" />
                <div className="flex-1 h-[2px] bg-gray-100 mx-4" />
                <StepMarker step={2} current={step} label="Organization" />
                <div className="flex-1 h-[2px] bg-gray-100 mx-4" />
                <StepMarker step={3} current={step} label="Meter" />
                <div className="flex-1 h-[2px] bg-gray-100 mx-4" />
                <StepMarker step={4} current={step} label="License" />
                <div className="flex-1 h-[2px] bg-gray-100 mx-4" />
                <StepMarker step={5} current={step} label="Review" />
            </div>

            {error && (
                <div className="mb-8 p-4 bg-red-50 border border-red-100 text-red-600 flex items-center gap-3 animate-shake">
                    <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                    <p className="text-xs font-black uppercase tracking-widest">{error}</p>
                </div>
            )}

            <div className="glass-card bg-white border border-gray-200 shadow-2xl p-12 min-h-[500px] flex flex-col">
                <div className="flex-1">
                    {step === 1 && (
                        <div className="animate-fade-in space-y-8">
                            <div className="flex items-center gap-3 mb-6">
                                <BuildingOfficeIcon className="w-6 h-6 text-[#155e75]" />
                                <h3 className="text-xl font-bold text-gray-800 uppercase tracking-tight">Technical Identification</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <Input label="Device Name" name="deviceName" value={formData.deviceName} onChange={handleChange} placeholder="e.g. Solar Unit A" />
                                <Input label="GSRN (18-Digit)" name="gsrn" value={formData.gsrn} onChange={handleChange} placeholder="GS1 standard" fontMono />
                                <Select label="Energy Source" name="energySource" value={formData.energySource} onChange={handleChange}>
                                    <option value="F01010101">Solar Fuel (Primary)</option>
                                    <option value="F01020202">Wind Fuel</option>
                                    <option value="F01030303">Water Fuel (Hydro)</option>
                                    <option value="F01040404">Geothermal Energy</option>
                                    <option value="F01050505">Aerothermal Energy</option>
                                    <option value="F04000000">Biomass (Solid/Liquid/Gas)</option>
                                </Select>
                                <Select label="Energy Technology" name="techType" value={formData.techType} onChange={handleChange}>
                                    <option value="T010000">Solar Photovoltaic (PV)</option>
                                    <option value="T010100">Solar Thermal (Concentrated)</option>
                                    <option value="T020000">Wind Onshore</option>
                                    <option value="T020001">Wind Offshore</option>
                                    <option value="T030100">Hydro Run-of-river</option>
                                    <option value="T030200">Hydro Reservoir (Dam)</option>
                                    <option value="T040000">Combined Heat & Power (CHP)</option>
                                </Select>
                                <Input label="Capacity (MW)" name="capacity" value={formData.capacity} onChange={handleChange} type="number" />
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="animate-fade-in space-y-8">
                            <div className="flex items-center gap-3 mb-6">
                                <MapPinIcon className="w-6 h-6 text-[#155e75]" />
                                <h3 className="text-xl font-bold text-gray-800 uppercase tracking-tight">Geographical Data</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <Input className="md:col-span-2" label="Street Address" name="address" value={formData.address} onChange={handleChange} />
                                <Input label="City" name="city" value={formData.city} onChange={handleChange} />
                                <Select label="Jurisdiction" name="country" value={formData.country} onChange={handleChange}>
                                    {countries.map(c => (
                                        <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
                                    ))}
                                </Select>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="animate-fade-in space-y-8">
                            <div className="flex items-center gap-3 mb-6">
                                <TicketIcon className="w-6 h-6 text-[#155e75]" />
                                <h3 className="text-xl font-bold text-gray-800 uppercase tracking-tight">Metering Infrastructure</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <Input label="Manufacturer" name="meterManufacturer" value={formData.meterManufacturer} onChange={handleChange} />
                                <Input label="Model" name="meterModel" value={formData.meterModel} onChange={handleChange} />
                                <Input label="Serial Number" name="meterSerial" value={formData.meterSerial} onChange={handleChange} fontMono />
                                <Select label="Grid Operator (TSO/DSO)" name="gridOperatorName" value={formData.gridOperatorName} onChange={handleChange}>
                                    <option value="ONEE (National Grid)">ONEE (National Grid Branch)</option>
                                    <option value="Lydec (Casablanca)">Lydec (Casablanca DSO)</option>
                                    <option value="Redal (Rabat)">Redal (Rabat DSO)</option>
                                    <option value="Amendis (Tangier/Tetouan)">Amendis (DSO)</option>
                                    <option value="Independent Auditor (Law 13-09)">Independent Auditor (Direct Line)</option>
                                </Select>
                                <div className="flex items-center gap-4 bg-gray-50 p-4 border border-gray-100">
                                    <input type="checkbox" name="auxiliaryDeduction" checked={formData.auxiliaryDeduction} onChange={handleChange} className="w-5 h-5 accent-[#155e75]" />
                                    <div>
                                        <p className="text-[10px] font-black text-gray-800 uppercase tracking-widest">Auxiliary Power Deduction</p>
                                        <p className="text-[9px] text-gray-400 font-bold">Automatic internal consumption offset enabled</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="animate-fade-in space-y-8">
                            <div className="flex items-center gap-3 mb-6">
                                <ShieldCheckIcon className="w-6 h-6 text-[#155e75]" />
                                <h3 className="text-xl font-bold text-gray-800 uppercase tracking-tight">Licensing & Standards</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <Input label="Commissioning Date" name="commissionedDate" value={formData.commissionedDate} onChange={handleChange} type="date" />
                                <Select label="Support Scheme" name="supportScheme" value={formData.supportScheme} onChange={handleChange}>
                                    <option value="NONE">No Support (Market Based)</option>
                                    <option value="FEED_IN_TARIFF">Feed-in Tariff (FiT)</option>
                                    <option value="INVESTMENT_GRANT">Capital Investment Grant</option>
                                    <option value="PRODUCTION_GRANT">Production Based Subsidy</option>
                                    <option value="TAX_CREDIT">National Tax Credit</option>
                                </Select>
                                <Select label="Quality Label" name="labelScheme" value={formData.labelScheme} onChange={handleChange}>
                                    <option value="NONE">No Label</option>
                                    <option value="EKOENERGY">EKOenergy (Global Standard)</option>
                                    <option value="GREEN_E">Green-e (US Renewable)</option>
                                    <option value="NATUREMADE">Naturemade (Swiss Star)</option>
                                    <option value="TUV_SUD">TUV SÜD CMS 71 (European)</option>
                                </Select>
                                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className={`p-4 border transition-all flex items-center justify-between ${formData.isAutoproduction ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-100'}`}>
                                        <div className="flex flex-col">
                                            <span className="text-[10px] font-black text-gray-800 uppercase tracking-widest">Autoproduction</span>
                                            <span className="text-[9px] text-gray-400 font-bold">Law 82-21 (BTM Gen)</span>
                                        </div>
                                        <input type="checkbox" name="isAutoproduction" checked={formData.isAutoproduction} onChange={handleChange} className="w-5 h-5 accent-[#155e75]" />
                                    </div>
                                    <div className={`p-4 border transition-all flex items-center justify-between ${formData.isDirectLine ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-100'}`}>
                                        <div className="flex flex-col">
                                            <span className="text-[10px] font-black text-gray-800 uppercase tracking-widest">Direct Line</span>
                                            <span className="text-[9px] text-gray-400 font-bold">Law 13-09 Export</span>
                                        </div>
                                        <input type="checkbox" name="isDirectLine" checked={formData.isDirectLine} onChange={handleChange} className="w-5 h-5 accent-[#155e75]" />
                                    </div>
                                    <div className={`p-4 border transition-all flex items-center justify-between ${formData.h2Ready ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-100'}`}>
                                        <div className="flex flex-col">
                                            <span className="text-[10px] font-black text-emerald-800 uppercase tracking-widest">H2-Ready Asset</span>
                                            <span className="text-[9px] text-emerald-600/60 font-bold italic">Offre Maroc Project</span>
                                        </div>
                                        <input type="checkbox" name="h2Ready" checked={formData.h2Ready} onChange={handleChange} className="w-5 h-5 accent-emerald-600" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 5 && (
                        <div className="animate-fade-in space-y-8">
                            <div className="flex items-center gap-3 mb-6">
                                <CheckBadgeIcon className="w-6 h-6 text-emerald-500" />
                                <h3 className="text-xl font-bold text-gray-800 uppercase tracking-tight">Final Manifest Review</h3>
                            </div>
                            <div className="bg-gray-50 p-8 border border-gray-100 grid grid-cols-2 gap-y-6">
                                <ReviewRow label="Asset Name" value={formData.deviceName} />
                                <ReviewRow label="GSRN ID" value={formData.gsrn} />
                                <ReviewRow label="Energy Source" value={formData.energySource} />
                                <ReviewRow label="Technology" value={formData.techType} />
                                <ReviewRow label="Capacity" value={`${formData.capacity} MW`} />
                                <ReviewRow label="Meter" value={formData.meterSerial} />
                                <ReviewRow label="Location" value={formData.city} />
                                <ReviewRow label="Jurisdiction" value={formData.country} />
                                <ReviewRow label="Support Scheme" value={formData.supportScheme} />
                                <ReviewRow label="Quality Label" value={formData.labelScheme} />
                            </div>
                            <p className="text-[10px] text-gray-400 italic font-medium leading-relaxed">
                                By submitting this manifest, you certify that the technical parameters provided are accurate and comply with the latest EECS Principles & Rules. Biometric logs will be recorded upon submission.
                            </p>
                        </div>
                    )}
                </div>

                <div className="flex justify-between items-center mt-12 pt-8 border-t border-gray-100">
                    <button
                        onClick={prevStep}
                        disabled={step === 1 || loading}
                        className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all ${step === 1 ? 'opacity-0' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                        <ArrowLeftIcon className="w-4 h-4" /> Back
                    </button>

                    {step < 5 ? (
                        <button
                            onClick={nextStep}
                            className="flex items-center gap-2 bg-[#155e75] text-white px-10 py-4 rounded-sm text-[10px] font-black uppercase tracking-widest hover:bg-[#0e4e61] transition-all shadow-xl"
                        >
                            Continue <ArrowRightIcon className="w-4 h-4" />
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="flex items-center gap-3 bg-emerald-600 text-white px-12 py-4 rounded-sm text-[10px] font-black uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-xl"
                        >
                            {loading ? (
                                <> <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Transmitting...</>
                            ) : (
                                <> <CloudArrowUpIcon className="w-5 h-5" /> Submit Registry Manifest</>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

const Input = ({ label, fontMono, className, ...props }) => (
    <div className={`space-y-2 ${className}`}>
        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">{label}</label>
        <input {...props} className={`w-full p-4 bg-gray-50 border border-gray-200 outline-none focus:border-[#155e75] focus:bg-white transition-all text-xs font-bold text-gray-700 rounded-none ${fontMono ? 'font-mono' : ''}`} />
    </div>
);

const Select = ({ label, children, ...props }) => (
    <div className="space-y-2">
        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">{label}</label>
        <select {...props} className="w-full p-4 bg-gray-50 border border-gray-200 outline-none focus:border-[#155e75] focus:bg-white transition-all text-xs font-bold text-gray-700 rounded-none appearance-none">
            {children}
        </select>
    </div>
);

const ReviewRow = ({ label, value }) => (
    <div>
        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">{label}</p>
        <p className="text-xs font-black text-[#155e75] uppercase">{value || 'NOT SPECIFIED'}</p>
    </div>
);
